import React, {useEffect, useRef, useState} from "react";
import { SUPPORTED_LANGUAGES } from "../../lib/Languages";
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Text,
  Tooltip,
  useColorMode,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPen} from "@fortawesome/free-solid-svg-icons";
import ChangePasswordComponent from "../ChangePassword/ChangePasswordComponent";
import {Auth, cqAPI, getParam, HOST} from "../../lib/Auth";
import {useAppContext} from "../../AppContext";
import {emailValidator, fullNameValidator, phoneValidator, nomailValidator} from "../../lib/Validator";
import {Autocomplete, useJsApiLoader} from "@react-google-maps/api";
import ReactDatePicker from "react-datepicker";
import moment from 'moment-timezone';
import { EmailIcon } from "@chakra-ui/icons";
import {normalizeGoogleAddress} from "../../lib/Location";
import { WORKER_DENTICON } from "../AdapterSetting/constants";

const libraries = ["places"];

function ProfileSettings() {
  const {
    user,
    isPatient,
    isProfileChanged,
    setIsProfileChanged
  } = useAppContext();

  const googleMap = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries
  });
  const googleMapsOptions = {
    fields: ['formatted_address', 'address_components'],
    componentRestrictions: {
      country: 'us'
    },
    types: ['street_address', 'premise']
  };

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [gender, setGender] = useState('');
  const [location, setLocation] = useState('');
  const [dob, setDOB] = useState('');
  const [nameUpdated, setNameUpdated] = useState(false);
  const [phoneUpdated, setPhoneUpdated] = useState(false);
  const [emailUpdated, setEmailUpdated] = useState(false);
  const [dobUpdated, setDOBUpdated] = useState(false);
  const [genderUpdated, setGenderUpdated] = useState(false);
  const [locationUpdated, setLocationUpdated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [originalPassword, setOriginalPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const {colorMode, toggleColorMode} = useColorMode();
  const [isImageUpdated, setIsImageUpdated] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);
  const fileInputRef = useRef(null);
  const autocompleteRef = useRef({});
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isDenticon, setIsDenticon] = useState(false);
  const [tfa, setTfa] = useState(false);
  const isSuperAdmin = Auth.user?.isSuperAdmin;
  const [showAddress, setShowAddress] = useState(false);
  const [isInboxGroupedByReplyStatus, setIsInboxGroupedByReplyStatus] = useState(false);
  const [inboxMethodUpdated, setInboxMethodUpdated] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [preferredLanguageUpdated, setPreferredLanguageUpdated] = useState(false);

  useEffect(() => {
    if (passwordInvalid || !isProfileChanged) {
      setUpdateButtonDisabled(true);
    } else {
      setUpdateButtonDisabled(false);
    }
  }, [passwordInvalid, isProfileChanged]);

  const handleFileUpload = (event) => {
    setIsProfileChanged(true);
    const file = event.target.files[0];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setProfileImage(fileUrl); // Display the temporary URL immediately
      setIsImageUpdated(true);
    }

    const formData = new FormData();
    formData.append("file", file, encodeURIComponent(file.name));
    cqAPI.post("/upload", formData)
      .then(response => {
        if (response.type === "ok") {
          setProfileImage(`${HOST}${response.url}`); // Update the profile image URL after successful upload
          toast({
            title: "Profile image uploaded successfully.", status: "success", duration: 9000, isClosable: true,
          });
        } else {
          toast({
            title: "Error uploading profile image.",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      })
      .catch(error => {
        toast({
          title: "Error uploading profile image.",
          description: error.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      });
  };

  const getPatientInfo = async () => {
    const response = await cqAPI.get('/isloggedin?refresh=1')
    setName(response.data.user.name);
    setPhone(response.data.user.phone);
    if (!nomailValidator.pattern.test(response.data.user.email)) {
      setEmail(response.data.user.email);
      setSavedEmail(response.data.user.email);
    } else {
      setSavedEmail('');
    }
    setProfileImage(response.data.user.picture);
    setGender(response.data.user.gender);
    setLocation(response.data.user.location);
    setDOB(response.data.user.dob);
    setTfa(response.data.user.two_factor_auth?.enabled || false);
    setHasPassword(response.data.user.has_password !== false);
    setProfileImage(response.data.user.picture);
    setShowAddress(response.data.user.singlePatient);
    setIsInboxGroupedByReplyStatus(response.data.user.inbox_grouped_by_reply_status);
    setPreferredLanguage(response.data.user.preferred_language || 'en');
    setNameUpdated(false);
    setPhoneUpdated(false);
    setEmailUpdated(false);
    setIsProfileChanged(false);
    setPasswordInvalid(false);
    setIsImageUpdated(false);
    setDOBUpdated(false);
    setGenderUpdated(false);
    setLocationUpdated(false);
    setInboxMethodUpdated(false);
    setPreferredLanguageUpdated(false);
    if (response.data.user.business?.pms_type === WORKER_DENTICON) {
      setIsDenticon(true);
    } else {
      setIsDenticon(false);
    }
  }

  useEffect(() => {
    if (!showPassword) {
      setPasswordInvalid(false);
    }
  }, [showPassword])

  useEffect(() => {
    localStorage.setItem("chakra-ui-color-mode", colorMode);
  }, [colorMode]);

  useEffect(() => {
    getPatientInfo();
  }, [user]);

  const showAlert = (message) => {
    toast({
      title: 'Profile error',
      description: message,
      status: 'error',
      duration: 9000,
      isClosable: true
    });
  }

  const processUpdate = (response) => {
    const {msg} = response;
    if (response.type === "ok") {
      // if impersonating, do not update the user in localstorage
      if (!getParam('_uid')) {
        Auth.storeUser({
          ...Auth.user,
          name: name, ...(phone || phoneUpdated ? {phone} : {}),
          picture: profileImage,
          gender,
          location,
          dob,
          inbox_grouped_by_reply_status: isInboxGroupedByReplyStatus
        });
      }
      toast({
        title: 'Success',
        description: msg || 'Profile updated successfully',
        status: 'success',
        duration: 9000,
        isClosable: true
      });
      setIsProfileChanged(false);
      setNameUpdated(false);
      setPhoneUpdated(false);
      setEmailUpdated(false);
      setIsImageUpdated(false);
      setDOBUpdated(false);
      setGenderUpdated(false);
      setLocationUpdated(false);
      setInboxMethodUpdated(false);
      setPreferredLanguageUpdated(false);
    } else {
      showAlert(msg || "Profile update failed");
    }
  };

  async function updateProfile() {
    try {

      let stop;
      if (nameUpdated && !fullNameValidator.pattern.test(name.trim())) {
        toast({
          title: "Error",
          description: fullNameValidator.invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        stop = true;
      }
      if (phoneUpdated && !phoneValidator.pattern.test(phone)) {
        toast({
          title: "Error",
          description: phoneValidator.invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        stop = true;
      }
      if (emailUpdated && !emailValidator.pattern.test(email)) {
        toast({
          title: "Error",
          description: emailValidator.invalid,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        stop = true;
      }
      if (stop) {
        return;
      }

      const response = await cqAPI.post("/profile", {
        name: nameUpdated ? name.trim() : undefined,
        phone: phoneUpdated ? phone : undefined,
        email: emailUpdated ? email : undefined,
        ...(password ? {originalPassword, password} : {}),
        twoFactorAuth: tfa,
        picture: isImageUpdated ? profileImage : undefined,
        gender: genderUpdated ? gender : undefined,
        location: locationUpdated ? location : undefined,
        dob: dobUpdated ? dob : undefined,
        inbox_grouped_by_reply_status: inboxMethodUpdated? isInboxGroupedByReplyStatus: undefined,
        preferred_language: preferredLanguageUpdated ? preferredLanguage : undefined
      });

      processUpdate(response);
    } catch (error) {
      console.error("Error updating profile: ", error);
    }
  }

  const handleThemeChange = () => {
    setIsProfileChanged(true);
    toggleColorMode();
  }

  const handle2FAChange = (value) => {
    setIsProfileChanged(true);
    setTfa(value);
  }

  const handlePlaceSelect = async () => {
    const place = autocompleteRef.current.getPlace();
    const normalizedAddress = normalizeGoogleAddress(place?.address_components);

    if (place?.formatted_address && normalizedAddress) {
      setLocation(normalizedAddress);
      setIsProfileChanged(true);
      setLocationUpdated(true);
    } else {
      showAlert("Please select an address that includes a ZIP code.");
    }
  };

  const handleDOBSelect = (date) => {
    setIsProfileChanged(true);
    setDOBUpdated(true);
    setDOB(date.toISOString());
  }

  const getDOB = () => {
    if (!dob) {
      return null;
    }
    const parsed = moment(dob, "YYYY-MM-DD");
    if (parsed.isValid()) {
      return parsed.toDate();
    }

    return null;
  }

  const processReset = (response) => {
    const { msg } = response;
    if (response.type === "ok") {
      toast({
        title: "Password reset sent",
        description: msg || "success",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else {
      showAlert(msg || "Password reset failed");
    }
  };

  const submitReset = async (event) => {
    onClose();
    event.preventDefault();
    try {
      const response = await Auth.requestPasswordReset(savedEmail);
      processReset(response);
    } catch (error) {
      showAlert(`Error during password reset: ${error.message}`);
    }
  };

  return (
    <Box className="profileEditor" p={4} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
      <VStack spacing={4}>
        <Flex align="top" w="full">
          <Box position="relative" verticalAlign="top">
            <Avatar size="xl" src={profileImage}/>
            <IconButton
              icon={<FontAwesomeIcon icon={faPen}/>}
              aria-label="profile picture"
              position="relative"
              size="sm"
              onClick={() => fileInputRef.current.click()}
            />
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*" // Only accept image files
              onChange={handleFileUpload}
              hidden
            />
          </Box>
          <Flex direction="column" w="xl">
            <FormControl id="name" ml={4} flex="1">
              <FormLabel>Name:</FormLabel>
              <Input
                type="text"
                value={name}
                onChange={(e) => {
                  setIsProfileChanged(true);
                  if (e.target.value !== name) {
                    setNameUpdated(true);
                    setName(e.target.value);
                  }
                }}
              />
            </FormControl>
            <FormControl id="phone" mt={4} ml={4} flex="1">
              <FormLabel>Phone:</FormLabel>
              <Input
                type="number"
                value={phone}
                onChange={(e) => {
                  setIsProfileChanged(true);
                  if (e.target.value !== phone) {
                    setPhoneUpdated(true);
                    setPhone(e.target.value);
                  }
                }}
              />
            </FormControl>
            <FormControl id="email" mt={4} ml={4} flex="1">
              <FormLabel>Email:</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setIsProfileChanged(true);
                  if (e.target.value !== email) {
                    setEmailUpdated(true);
                    setEmail(e.target.value);
                  }
                }}
              />
            </FormControl>
            {isPatient && (
              <>
                <FormControl id="dob" className="dob" mt={4} ml={4} flex="1" as="fieldset">
                  <FormLabel as="legend">Date of Birth:</FormLabel>
                  <ReactDatePicker
                    selected={getDOB()}
                    onSelect={handleDOBSelect}
                    onChange={handleDOBSelect}
                    maxDate={new Date()}
                    strictParsing
                    className="profileDatePicker"
                    wrapperClassName={colorMode === "light" ? "light-mode" : "dark-mode"}
                  />
                </FormControl>
                <FormControl id="gender" mt={4} ml={4} flex="1" as="fieldset">
                  <FormLabel as="legend">Gender:</FormLabel>
                  <RadioGroup
                    onChange={(e) => {
                      setIsProfileChanged(true);
                      setGender(e);
                      setGenderUpdated(true);
                    }}
                    value={gender}
                  >
                    <HStack spacing="24px">
                      <Radio value="Male">Male</Radio>
                      <Radio value="Female">Female</Radio>
                      {isDenticon && <Radio value="Unspecified">Unspecified</Radio> }
                    </HStack>
                  </RadioGroup>
                </FormControl>
                {showAddress && (<FormControl id="google-maps-autocomplete" mt={4} ml={4} flex="1" paddingTop="15px">
                  <FormLabel>Address</FormLabel>
                  {googleMap.isLoaded && (
                    <Autocomplete
                      className="location-autocomplete"
                      options={googleMapsOptions}
                      onPlaceChanged={handlePlaceSelect}
                      onLoad={(autocomplete) => {
                        autocompleteRef.current = autocomplete;
                      }}
                    >
                      <Input
                        type="text"
                        placeholder={'Type to search...'}
                        value={location}
                        onChange={(e) => {
                          setLocation(e.target.value)
                        }}
                      />
                    </Autocomplete>
                  )}
                </FormControl>)}
              </>
            )}
            <Flex justify="right" w="full" direction="row" mt={4} ml={4}>
              <Button
                id="update-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                  if (!hasPassword && !showPassword && !toast.isActive("no-password-toast")) {
                    toast({
                      id: "no-password-toast",
                      title: "No password on file",
                      description: 'Your account does not have a password yet. Select "Forgot Password?" to receive an email with instructions to set one.',
                      status: "info",
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                }}
              >
                {showPassword ? "Don't update password" : "Update password"}
              </Button>
            </Flex>
            {showPassword && (<Flex justify="right" w="full" direction="row" mt={4} ml={4}>
              <>
              <Link
                onClick={onOpen}
              >
                Forgot Password?
              </Link>

                <Modal isOpen={isOpen}
                       onClose={onClose}
                       isCentered
                       motionPreset='slideInBottom'
                >
                  <ModalOverlay />
                  <ModalContent  mx={8}>
                    <ModalHeader alignSelf="center">Reset Password</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Flex direction="column" spacing={4} alignItems="center" textAlign="center">
                        <Text fontWeight="400">Confirm below and we&apos;ll send an email with your password reset instructions.</Text>
                        <Button
                          leftIcon={<EmailIcon/>}
                          my={5}
                          width="280px"
                          onClick={

                            submitReset
                          }
                        >
                          Confirm Password Reset
                        </Button>
                      </Flex>
                    </ModalBody>
                  </ModalContent>
                </Modal>
              </>
            </Flex>)}
            <Flex w="full" direction="row" mt={4} ml={4}>
              <ChangePasswordComponent
                isOriginalRequired
                isVisible={showPassword && hasPassword}
                onPasswordChange={(pwd) => {
                  setPassword(pwd);
                  setIsProfileChanged(true);
                }}
                onOriginalPasswordChange={(pwd) => {
                  setOriginalPassword(pwd);
                }}
                onIsInvalidChange={setPasswordInvalid}
              />
            </Flex>
            {!isPatient && <FormControl id="inbox-method" mt={4} ml={4} flex="1" as="fieldset">
              <Tooltip label="This change applies only to your personal inbox. If your team wants to use this feature,
               each member will need to enable it in their own settings" placement="bottom-end" hasArrow>
                <FormLabel as="legend">Inbox: Separate open conversations into
                  <Text as="span" fontStyle="italic"> Unanswered</Text> and{" "}
                  <Text as="span" fontStyle="italic">Answered</Text>
                </FormLabel>
              </Tooltip>
              <Flex align="center" w="full">
                <Switch
                  isChecked={isInboxGroupedByReplyStatus}
                  onChange={e => {
                    setIsProfileChanged(true);
                    setInboxMethodUpdated(true);
                    setIsInboxGroupedByReplyStatus(e.target.checked);
                  }}
                />
              </Flex>
            </FormControl>}
            <FormControl id="preferred-language" mt={4} ml={4} flex="1">
              <FormLabel>Preferred Language:</FormLabel>
              <Select
                value={preferredLanguage}
                onChange={(e) => {
                  setIsProfileChanged(true);
                  setPreferredLanguage(e.target.value);
                  setPreferredLanguageUpdated(true);
                }}
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang.value} value={lang.value}>{lang.name}</option>
                ))}
              </Select>
            </FormControl>

            {!isSuperAdmin && <FormControl id="2fa" mt={4} ml={4} flex="1" as="fieldset">
              <FormLabel as="legend">Two Factor Authentication:</FormLabel>
              <Flex align="center" w="full">
                <Text fontSize="sm" mr={2}>
                  Disabled
                </Text>
                <Switch
                  isChecked={tfa}
                  onChange={e => handle2FAChange(e.target.checked)}
                  colorScheme="blue"
                />
                <Text fontSize="sm" ml={2}>
                  Enabled
                </Text>
              </Flex>
            </FormControl>}
            <FormControl id="theme" mt={4} ml={4} flex="1"  as="fieldset">
              <FormLabel as="legend">Theme:</FormLabel>
              <Flex align="center" w="full">
                <Text fontSize="sm" mr={2}>
                  Light
                </Text>
                <Switch
                  id="theme-toggle"
                  isChecked={colorMode === "dark"}
                  onChange={handleThemeChange}
                  colorScheme="blue"
                />
                <Text fontSize="sm" ml={2}>
                  Dark
                </Text>
              </Flex>
            </FormControl>
            <Flex justify="right" w="full" mt={4} ml={4}>
              <Button
                id="update-profile"
                mr={3}
                onClick={updateProfile}
                isDisabled={updateButtonDisabled}
              >
                Update Profile
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </VStack>
    </Box>
  );
}

export default ProfileSettings;
